// FindGeeks design tokens — single source of truth for colors, typography,
// spacing, radii and shadows used by mobile (client + engineer themes) and
// the admin web console. Mirrors sub-plan 03 §3.2 (literal copy — see
// REF-ui-tokens.md R60: `font.sizes.X` is nested one level deeper than the
// REF doc shows; e.g. `tokens.font.sizes.uppercaseTitle`, NOT
// `tokens.font.uppercaseTitle`. Same convention applies to admin
// `colors.admin.tabActive` (camelCase flat — R48 finding) instead of the
// REF dotted form `admin.tab.active`).

export const tokens = {
  colors: {
    client: {
      bg: "#1E3A6E",
      surface: "#FFFFFF",
      primary: "#1E4CF8",
      primaryText: "#FFFFFF",
      accent: "#2DD4A8",
      textHeading: "#FFFFFF",
      textBody: "#1E3A6E",
      textMuted: "#90A4B7",
      link: "#1E4CF8",
      border: "#DBE3ED",
    },
    engineer: {
      bg: "#6156D8",
      surface: "#FFFFFF",
      primary: "#6156D8",
      secondary: "#1E4CF8",
      accent: "#22C55E",
      textHeading: "#1A1C3B",
      textBody: "#1A1C3B",
      textMuted: "#8C8FA8",
    },
    admin: {
      // Reference: TVP2 Final Report §9 image2 (Dashboard).
      // Top AppBar = teal/cyan; left Sidebar = WHITE; active item = teal.
      header: "#00BCD4",         // teal AppBar background (matches reference)
      headerText: "#FFFFFF",     // white text + icons in AppBar
      sidebar: "#FFFFFF",        // white sidebar background
      sidebarBrand: "#00BCD4",   // teal "Find Geeks" wordmark (was navy)
      sidebarText: "#424242",    // dark grey for inactive items (was white)
      sidebarActive: "#00BCD4",  // teal for active item (was royal blue)
      sidebarHover: "#F5F7FA",   // subtle grey hover highlight
      bg: "#F5F7FA",
      surface: "#FFFFFF",
      primary: "#00BCD4",        // teal primary buttons (Search etc.)
      primaryHover: "#00ACC1",   // slightly darker teal on hover
      success: "#00C853",
      danger: "#D32F2F",
      textHeading: "#212121",
      textBody: "#424242",
      textMuted: "#9E9E9E",
      border: "#E0E0E0",
      tabActive: "#00BCD4",      // active tab underline (R48 token, now teal)
      // Sign-in page splash (image1: dark charcoal w/ teal+grey diagonal accents).
      signInBg: "#1A1A1A",
      signInAccentTeal: "#00BCD4",
      signInAccentGrey: "#9E9E9E",
    },
    shared: {
      ratingGold: "#F5B400",
      ratingGrey: "#D0D0D0",
      errorRed: "#E53935",
      statusIdle: "#22C55E",
      mapCustomer: "#EA4335",
      mapEngineer: "#1A73E8",
    },
  },
  font: {
    family:
      "Inter, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif",
    sizes: {
      display: 48,
      h1: 28,
      h2: 22,
      h3: 18,
      buttonLg: 18,
      body: 16,
      bodySm: 14,
      label: 13,
      caption: 12,
      uppercaseTitle: 20,
    },
    weights: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  },
  space: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 48 },
  radius: { sm: 8, md: 12, lg: 16, pill: 9999, chip: 9999 },
  shadow: {
    card: "0 1px 2px rgba(0,0,0,0.05), 0 2px 8px rgba(0,0,0,0.06)",
    modal: "0 10px 30px rgba(0,0,0,0.15)",
    fab: "0 4px 12px rgba(30,76,248,0.3)",
  },
} as const;

export type Tokens = typeof tokens;

/**
 * Helper to build a MUI v5 theme from admin tokens. Returned object is a
 * plain object literal so we don't take a runtime dependency on `@mui/material`
 * here in `@findgeeks/ui-tokens`. The admin app passes this to
 * `createTheme(...)`.
 */
export function buildAdminMuiTheme() {
  return {
    palette: {
      primary: { main: tokens.colors.admin.primary },
      success: { main: tokens.colors.admin.success },
      error: { main: tokens.colors.admin.danger },
      background: {
        default: tokens.colors.admin.bg,
        paper: tokens.colors.admin.surface,
      },
    },
    typography: {
      fontFamily: tokens.font.family,
      h1: {
        fontSize: tokens.font.sizes.h1,
        fontWeight: tokens.font.weights.bold,
      },
      h2: {
        fontSize: tokens.font.sizes.h2,
        fontWeight: tokens.font.weights.semibold,
      },
      h3: {
        fontSize: tokens.font.sizes.h3,
        fontWeight: tokens.font.weights.semibold,
      },
      body1: { fontSize: tokens.font.sizes.body },
      caption: { fontSize: tokens.font.sizes.caption },
    },
    shape: { borderRadius: tokens.radius.sm },
  };
}
